// Navigation Links
// Primary navigation — every item points to a real section (#) or page (/).
export const NAV_LINKS = [
  { id: 'capabilities', name: 'Services', href: '#capabilities' },
  { id: 'work', name: 'Work', href: '#work' },
  { id: 'lab', name: 'Lab', href: '/lab' },
  { id: 'process', name: 'Process', href: '#process' },
  { id: 'team', name: 'Team', href: '#team' },
  { id: 'apps', name: 'Apps', href: '/apps' },
  { id: 'blog', name: 'Blog', href: '/blog' },
  { id: 'contact', name: 'Contact', href: '#contact' },
];

// Services Data — with starting rates
export const SERVICES = [
  {
    title: 'iOS Development',
    description:
      "Native iOS apps built with SwiftUI and UIKit. We've shipped 15 production apps across fitness, finance, productivity, and sports — with StoreKit 2 subscriptions, CloudKit sync, and App Store optimization baked in.",
    icon: 'Smartphone',
    stat: '15 apps shipped',
    startingRate: 'From $3,000',
    tech: ['Swift', 'SwiftUI', 'StoreKit 2', 'CloudKit'],
  },
  {
    title: 'Full-Stack Web',
    description:
      'Production web applications with React/Next.js frontends and robust Node.js or Python backends. Real-time features, authentication, payment integration, and deployment pipelines included.',
    icon: 'Code',
    stat: '4 platforms in production',
    startingRate: 'From $5,000',
    tech: ['React', 'Next.js', 'Node.js', 'PostgreSQL'],
  },
  {
    title: 'AI/ML Integration',
    description:
      'Not chatbot wrappers — real AI systems. Custom agents, automated workflows, intelligent pipelines, and LLM-powered features that solve actual business problems.',
    icon: 'BrainCircuit',
    stat: '6 AI systems in production',
    startingRate: 'From $8,000',
    tech: ['Python', 'LLM APIs', 'FastAPI', 'Custom Agents'],
  },
  {
    title: 'Trading Platforms',
    description:
      'Algorithmic trading systems, real-time market data pipelines, and financial applications built for speed. Our Axyonex platform handles multi-broker execution with sub-millisecond latency in Rust.',
    icon: 'CandlestickChart',
    stat: 'Sub-ms execution',
    startingRate: 'From $15,000',
    tech: ['Rust', 'WebSocket', 'React', 'PostgreSQL'],
  },
  {
    title: 'UI/UX Design',
    description:
      'Design that converts. Brand systems, interface design, prototyping, and design-to-code implementation. Every pixel intentional, every interaction considered.',
    icon: 'Lightbulb',
    stat: 'Brand systems for 15+ products',
    startingRate: 'From $2,000',
    tech: ['Figma', 'Design Systems', 'Motion Design', 'Prototyping'],
  },
  {
    title: 'Security & Infrastructure',
    description:
      'Network security platforms, threat detection, vulnerability scanning, and hardened infrastructure. We built Fortress because existing solutions weren\'t good enough.',
    icon: 'Shield',
    stat: 'Real-time monitoring',
    startingRate: 'From $10,000',
    tech: ['Python', 'FastAPI', 'scapy', 'nmap'],
  },
];

// Portfolio Projects — with subdomain links
export const PORTFOLIO_PROJECTS = [
  {
    title: 'Axyonex',
    subtitle: 'Algorithmic Trading Platform',
    description:
      'Professional-grade multi-broker trading platform. Rust execution engine with real-time WebSocket feeds, strategy backtesting, and automated position management across Kite, Fyers, and XTS APIs.',
    image: '/portfolio/axyonex/screenshot-1.png',
    category: 'Trading Platform',
    techStack: ['Rust', 'React', 'WebSocket', 'PostgreSQL'],
    status: 'In Development',
    highlight: 'Sub-millisecond order routing across 3 brokers',
    link: null, // subdomain not yet live
    problem:
      'Retail traders lack institutional-grade execution speed and multi-broker management.',
    approach:
      'Built a Rust execution engine for sub-ms latency, with React dashboard for real-time monitoring and strategy management.',
    result:
      'Multi-broker order routing, live strategy backtesting, and automated position management — all in one platform.',
  },
  {
    title: 'iOS App Suite',
    subtitle: '15 Apps Across 8 Categories',
    description:
      'Production app portfolio spanning fitness, finance, sports analytics, and productivity. Native SwiftUI, StoreKit 2 subscriptions, CloudKit sync. PackPal, TapLedger, Hydrate Daily, Prismatica, and more.',
    image: '/portfolio/ios-apps/collage.png',
    category: 'Mobile Development',
    techStack: ['Swift', 'SwiftUI', 'StoreKit 2', 'CloudKit'],
    status: 'Launching Q2 2026',
    highlight: '15 apps built, 8 categories, each with Pro tier',
    link: null, // iOS apps — no web demo available
    problem:
      'Fragmented utility apps with poor UX and aggressive monetization.',
    approach:
      'Designed a cohesive suite with shared design language, native SwiftUI, and fair Pro tier pricing.',
    result:
      '15 polished apps across health, finance, productivity, and sports — launching Q2 2026.',
  },
  {
    title: 'Fortress',
    subtitle: 'Network Security Platform',
    description:
      'Enterprise security monitoring with real-time packet inspection, automated threat detection, vulnerability scanning, and compliance reporting. Built for organizations that take security seriously.',
    image: '/portfolio/fortress/screenshot-1.png',
    category: 'Security',
    techStack: ['Python', 'FastAPI', 'scapy', 'nmap', 'React'],
    status: 'Beta',
    highlight: 'Real-time threat detection across network interfaces',
    link: null, // subdomain not yet live
    problem:
      'Most security tools are either expensive enterprise suites or fragmented open-source scripts.',
    approach:
      'Combined packet inspection, vulnerability scanning, and threat detection into a single real-time platform.',
    result:
      'Unified security dashboard with automated alerting, compliance reports, and actionable remediation steps.',
  },
  {
    title: 'Life-OS',
    subtitle: 'Health & Life Management Platform',
    description:
      'Comprehensive life and health management system with mobile app, web dashboard, and backend server. Tracks health metrics, daily routines, and personal goals in one unified platform.',
    image: '/portfolio/life-os/screenshot-1.png',
    category: 'Health-Tech',
    techStack: ['Swift', 'React', 'Node.js', 'CloudKit'],
    status: 'Production',
    highlight: 'Unified health tracking across iOS app + web dashboard',
    link: null, // subdomain not yet live
    problem:
      'Health and productivity apps are fragmented with no unified view of personal metrics and goals.',
    approach:
      'Built a full-stack platform with native iOS app for real-time tracking, React dashboard for insights, and Node.js backend for data synchronization.',
    result:
      'Complete health and life management system that seamlessly syncs personal metrics, routines, and goals across all devices.',
  },
  {
    title: 'Payments',
    subtitle: 'Payment Tracking Application',
    description:
      'Modern payment tracking and management application with a clean, responsive interface. Docker-containerized backend for reliable deployment with a fast Vite-powered frontend.',
    image: '/portfolio/payments/screenshot-1.png',
    category: 'Fintech',
    techStack: ['Vite', 'Tailwind CSS', 'Node.js', 'Docker'],
    status: 'Production',
    highlight: 'Docker-containerized architecture with real-time payment tracking',
    link: null, // subdomain not yet live
    problem:
      'Payment management tools are either too complex for small businesses or lack modern UX standards.',
    approach:
      'Built a streamlined application with Vite for fast development, Tailwind for clean UI, and Docker for reliable deployment infrastructure.',
    result:
      'Clean, fast payment tracking system with modern interface and containerized deployment for maximum reliability.',
  },
];

// Team Members — positioned as AI-augmented team (NOT revealing they are AI)
export const TEAM_MEMBERS = [
  {
    name: 'Tanush Gupta',
    role: 'Founder & CEO',
    emoji: '👤',
    type: 'founder',
    description:
      'Full-stack engineer who built trading systems, security tools, and 15 iOS apps. Architect of the entire operation and technical vision.',
    built: 'Architected Axyonex, Fortress, and the entire operation',
    techStack: ['Rust', 'Swift', 'Python', 'React'],
  },
  {
    name: 'Jacob',
    role: 'Project Manager',
    emoji: '👔',
    type: 'specialist',
    description:
      'Coordinates all projects, manages the team, handles client communication, and keeps everything running smoothly. The operational backbone.',
    built: 'End-to-end project coordination and delivery oversight',
    techStack: ['Project Management', 'Client Relations', 'Operations'],
  },
  {
    name: 'Rex',
    role: 'CTO & System Architect',
    emoji: '🏗️',
    type: 'specialist',
    description:
      'System architecture and technical strategy. Every project built to scale, not just to ship. Microservices, infrastructure, performance.',
    built: 'Microservices architecture behind Axyonex',
    techStack: ['Architecture', 'Rust', 'DevOps', 'PostgreSQL'],
  },
  {
    name: 'Koda',
    role: 'Lead Full-Stack Engineer',
    emoji: '⚡',
    type: 'specialist',
    description:
      'Full-stack implementation at incredible speed. Architecture diagrams become deployed, tested production code — fast.',
    built: 'Real-time WebSocket data pipeline for live trading',
    techStack: ['React', 'Node.js', 'Rust', 'TypeScript'],
  },
  {
    name: 'Iris',
    role: 'Lead UI/UX Designer',
    emoji: '💎',
    type: 'specialist',
    description:
      'UI/UX that converts. Every pixel intentional, every interaction considered. Brand systems, motion design, and interfaces that feel premium.',
    built: 'Feral Techlabs brand system and all app interfaces',
    techStack: ['Figma', 'CSS', 'Motion Design', 'Design Systems'],
  },
  {
    name: 'Stacy',
    role: 'Security Engineer',
    emoji: '🛡️',
    type: 'specialist',
    description:
      'Network security, penetration testing, and infrastructure hardening. Built the Fortress security platform from the ground up.',
    built: 'Fortress security platform + 24/7 network monitoring',
    techStack: ['Python', 'scapy', 'nmap', 'Metasploit'],
  },
  {
    name: 'Vance',
    role: 'Growth & Marketing',
    emoji: '📢',
    type: 'specialist',
    description:
      'Market positioning, content strategy, and growth engineering. Turns great software into revenue-generating products.',
    built: 'Go-to-market strategy for the iOS app suite',
    techStack: ['SEO', 'Analytics', 'Content Strategy'],
  },
  {
    name: 'Zara',
    role: 'QA & DevOps',
    emoji: '🔧',
    type: 'specialist',
    description:
      'CI/CD, automated testing, and infrastructure reliability. If it deploys, it works — every time.',
    built: 'Automated testing and deployment pipeline for all projects',
    techStack: ['Docker', 'GitHub Actions', 'Terraform', 'Testing'],
  },
];

// Technology Stack
export const TECHNOLOGIES = [
  { name: 'React', description: 'Our primary frontend framework for building responsive, high-performance web applications.' },
  { name: 'Swift', description: 'Native iOS development with SwiftUI for beautiful, performant mobile experiences.' },
  { name: 'Rust', description: 'High-performance systems programming for trading engines and latency-critical applications.' },
  { name: 'Python', description: 'AI/ML pipelines, security tools, and backend services with FastAPI.' },
  { name: 'Node.js', description: 'Server-side JavaScript for real-time applications and API services.' },
  { name: 'PostgreSQL', description: 'Reliable relational database for complex queries and data integrity.' },
  { name: 'Docker', description: 'Containerized deployments for consistent, reproducible infrastructure.' },
  { name: 'FastAPI', description: 'Modern Python API framework for high-performance backend services.' },
  { name: 'TypeScript', description: 'Type-safe JavaScript for large-scale, maintainable codebases.' },
  { name: 'Tailwind CSS', description: 'Utility-first CSS framework for rapid, consistent UI development.' },
];

// Blog Posts
export const BLOG_POSTS = [
  {
    id: 'why-we-built-an-ai-augmented-team',
    title: 'Why We Built an AI-Augmented Development Team',
    excerpt:
      "Most agencies scale by hiring. We scaled by augmenting our team with AI-powered tools and workflows. Here's why — and what we learned.",
    content: 'Most software agencies have the same playbook: get a client, hire developers, ship code, repeat. The bottleneck is always people.\n\nWe took a different approach. Instead of scaling headcount, we invested in AI-augmented workflows that multiply the output of every specialist on the team.\n\n## The Problem with Traditional Scaling\n\nTraditional agencies face a fundamental tension: quality drops as you scale. Every new hire needs onboarding. Every new project needs coordination overhead.\n\n## Our Approach\n\nEvery team member at Feral Techlabs is equipped with AI tools purpose-built for their domain. Our developers use AI-assisted code generation and review. Our designers use AI for rapid prototyping.\n\nThe result: an 8-person team that delivers the output of a 20-person agency — faster, and with fewer bugs.\n\n## What We Learned\n\n1. AI does not replace expertise — it amplifies it. Our developers still make every architectural decision.\n2. Speed enables quality. When you can iterate 3x faster, you can afford to be more thoughtful.\n3. Clients care about results, not headcount. Nobody asks how many people we have. They ask when it ships.',
    author: 'Tanush Gupta',
    date: '2026-03-15',
    readTime: '6 min',
    category: 'Engineering',
    tags: ['AI', 'Team Building', 'Startup'],
  },
  {
    id: 'rust-in-production-trading',
    title: 'Why We Chose Rust for Our Trading Engine',
    excerpt:
      "Sub-millisecond matters when real money is on the line. A technical deep-dive into building Axyonex's execution layer.",
    content: 'When we started building Axyonex, our multi-broker trading platform, we had one non-negotiable requirement: order execution had to be fast. Sub-millisecond.\n\n## Why Not Python? Why Not Node?\n\nPython is great for data science. Node.js is great for I/O-heavy web apps. Neither is great when you need deterministic, low-latency execution with zero garbage collection pauses.\n\n## Enter Rust\n\nRust gave us exactly what we needed: zero-cost abstractions, no garbage collector, memory safety via the borrow checker, and an excellent async runtime with Tokio.\n\n## The Architecture\n\nAxyonex\'s execution engine maintains persistent WebSocket connections to 3 broker APIs, normalizes order book data, routes orders to the optimal broker, and executes with sub-millisecond latency.\n\n## Results\n\nSub-millisecond order routing across 3 brokers. Zero production crashes in 6 months of testing. Memory usage stays flat under heavy market conditions. Rust was the right choice.',
    author: 'Rex',
    date: '2026-03-10',
    readTime: '10 min',
    category: 'Engineering',
    tags: ['Rust', 'Trading', 'Performance', 'Axyonex'],
  },
  {
    id: 'shipping-15-ios-apps',
    title: 'What We Learned Shipping 15 iOS Apps',
    excerpt:
      'SwiftUI patterns, StoreKit 2 gotchas, and the design system that made it possible to ship one app per week.',
    content: 'Shipping 15 iOS apps in under a year sounds insane. Here is how we actually did it — and what we would do differently.\n\n## The Design System First\n\nBefore writing a single line of SwiftUI, Iris built a shared design system: color tokens, typography scale, spacing grid, and reusable component patterns. Every app shares the same visual DNA while having its own personality.\n\n## SwiftUI Patterns That Scale\n\nWe settled on a consistent architecture: MVVM with protocol-oriented services. Each app has the same folder structure, the same networking layer, the same error handling. When you have built one, you have built the template for all fifteen.\n\n## StoreKit 2 Lessons\n\nStoreKit 2 is a massive improvement over the original API, but it still has sharp edges. Subscription status verification needs careful handling across app launches. We built a shared StoreKit manager that every app imports.\n\n## The Result\n\n15 apps across fitness, finance, productivity, and sports. Each with a free tier and a Pro subscription. Each with CloudKit sync. Each with a consistent, polished UI. Launching Q2 2026.',
    author: 'Koda',
    date: '2026-03-05',
    readTime: '8 min',
    category: 'Mobile',
    tags: ['iOS', 'SwiftUI', 'Mobile Development'],
  },
  {
    id: 'building-fortress-security-platform',
    title: 'Building Fortress: A Modern Security Platform',
    excerpt:
      "Real-time packet inspection, automated threat detection, and why existing security tools weren't good enough.",
    content: 'Existing security tools fall into two categories: expensive enterprise suites that require a dedicated team to operate, or fragmented open-source scripts that leave gaps everywhere. We built Fortress to be neither.\n\n## The Problem\n\nSmall to mid-size organizations need real security — not a checkbox compliance tool, not a dashboard that nobody checks. They need automated threat detection that actually tells them what to do when something is wrong.\n\n## What Fortress Does\n\nFortress combines real-time packet inspection (via scapy), automated vulnerability scanning (nmap integration), and threat intelligence feeds into a single dashboard. When it detects anomalous traffic or a known vulnerability, it generates actionable remediation steps — not just alerts.\n\n## The Tech Stack\n\nPython and FastAPI for the backend. React for the dashboard. scapy for packet capture. nmap for vulnerability scanning. All containerized with Docker for easy deployment on any infrastructure.\n\n## Where We Are\n\nFortress is in beta, running on our own infrastructure. Early results are promising: it detected a port scan attempt within 200ms and automatically generated a firewall rule recommendation.',
    author: 'Stacy',
    date: '2026-02-28',
    readTime: '9 min',
    category: 'Security',
    tags: ['Security', 'Python', 'Network', 'Fortress'],
  },
  {
    id: 'design-system-at-scale',
    title: 'Building a Design System for 15 Apps',
    excerpt:
      'How we created a cohesive visual language across web, iOS, and internal tools — with a lean team.',
    content: 'When you are building 15 apps, a website, and internal tools, inconsistency is the default. We made consistency the default instead by investing upfront in a design system.\n\n## Token-First Approach\n\nEverything starts with design tokens: colors, spacing, typography, shadows, and border radii defined in a single source of truth. These tokens translate into CSS custom properties for web and Swift extensions for iOS.\n\n## Component Library\n\nOn top of tokens, we built a component library. Buttons, cards, inputs, modals — each with variants, states, and accessibility baked in. The iOS version uses SwiftUI, the web version uses React with Tailwind.\n\n## The Feral Brand\n\nThe magenta accent, dark primary background, Inter for body text, JetBrains Mono for code — these are not just aesthetic choices. They are brand signals that say: modern, technical, premium.\n\n## Results\n\nNew apps take hours to theme instead of days. Design reviews are faster because the system handles 80 percent of decisions automatically. And every touchpoint — from the website to the iOS apps to internal dashboards — feels like it belongs to the same family.',
    author: 'Iris',
    date: '2026-02-20',
    readTime: '7 min',
    category: 'Design',
    tags: ['Design Systems', 'UI/UX', 'Branding'],
  },
  {
    id: 'ai-augmented-development',
    title: 'AI-Augmented Development: Lessons from 6 Months',
    excerpt:
      'How AI tools transformed our development speed, the pitfalls we avoided, and why the future of software is augmented.',
    content: 'Six months ago, we went all-in on AI-augmented development workflows. Not as a gimmick — as a core part of how we build software. Here is what actually worked and what did not.\n\n## What Worked\n\nCode generation for boilerplate: setting up API routes, database schemas, test scaffolding. AI handles the repetitive structure; we focus on business logic.\n\nCode review augmentation: AI catches style inconsistencies, potential bugs, and performance issues before human review. This cuts review time by roughly 40 percent.\n\nDocumentation generation: AI writes first-draft documentation from code comments and function signatures. We edit for accuracy and context.\n\n## What Did Not Work\n\nArchitectural decisions: AI suggestions for system design were often generic and missed important context about our specific constraints. Humans make these calls.\n\nComplex debugging: AI can help narrow down the search space, but debugging distributed systems still requires deep domain knowledge.\n\n## The Bottom Line\n\nAI-augmented development is not about replacing developers. It is about removing the friction that slows them down. Our developers spend more time thinking and less time typing. That is the real productivity gain.',
    author: 'Tanush Gupta',
    date: '2026-02-15',
    readTime: '11 min',
    category: 'AI',
    tags: ['AI', 'Development', 'Productivity', 'Lessons'],
  },
];

export const BLOG_CATEGORIES = [
  'All',
  'Engineering',
  'Mobile',
  'Security',
  'Design',
  'AI',
];

// Company Info
export const COMPANY_INFO = {
  name: 'Feral Techlabs',
  tagline: 'AI-Powered Software Development',
  address: 'Mumbai, India',
  phone: '+91 7506433022',
  email: 'jacob@feraltechlabs.co.in',
  contactEmails: [
    {
      name: 'Jacob',
      email: 'jacob@feraltechlabs.co.in',
      role: 'Project Manager',
      availability: 'Available 24/7',
    },
    {
      name: 'Tanush',
      email: 'tanushgupta@feraltechlabs.co.in',
      role: 'CEO',
      availability: 'Available on request',
    },
  ],
  businessHours: {
    weekdays: 'Always On — 24/7',
    weekends: 'Team never sleeps',
  },
  social: {
    github: 'https://github.com/feraltechlabs',
    linkedin: 'https://linkedin.com/company/feraltechlabs',
    twitter: 'https://twitter.com/feraltechlabs',
  },
};

// Contact Form Options
export const PROJECT_TYPES = [
  'iOS App',
  'Web Application',
  'AI Integration',
  'Trading Platform',
  'Security Tool',
  'UI/UX Design',
  'Custom Software',
  'Other',
];

export const BUDGET_RANGES = [
  'Under $5,000',
  '$5,000 – $15,000',
  '$15,000 – $50,000',
  '$50,000+',
  'Not sure yet',
];


// The Lab — live work-in-progress shown at /lab.
// Add a project = add an entry here. veil: true shows the codename + tagline
// instead of the real name (for projects not yet publicly announced).
export const LAB_PROJECTS = [
  {
    codename: 'PROJECT 001',
    title: null,
    veil: true,
    tagline: 'Something is being gathered.',
    description:
      'A quieter way to keep everything — built for people who think faster than they can file. Private by physics, not by promise: on-device intelligence, no accounts, no servers. iPhone first, then everywhere you are.',
    category: 'Consumer · Apple ecosystem',
    status: 'In build',
    eta: 'early 2027',
    progress: [
      { label: 'Design & blueprint', pct: 100 },
      { label: 'Foundations', pct: 4 },
      { label: 'The core loop', pct: 0 },
      { label: 'Polish & beta', pct: 0 },
    ],
    techStack: ['Swift 6', 'SwiftUI', 'On-device AI', 'CloudKit'],
    highlight: 'Name under wraps until the trademark clears — the reveal flips on right here.',
    link: null,
  },
  // Next up (template — uncomment and fill when ready to show):
  // {
  //   codename: 'PROJECT 002',
  //   title: 'Mahabharat',           // or veil it like above
  //   veil: false,
  //   tagline: 'An epic, playable.',
  //   description: '…',
  //   category: 'Game',
  //   status: 'In build',
  //   eta: 'TBA',
  //   progress: [{ label: 'Concept', pct: 100 }, { label: 'Build', pct: 10 }],
  //   techStack: ['…'],
  //   highlight: null,
  //   link: null,
  // },
];
